import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowRight,
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  Globe,
  User,
  Users,
  Lock,
  Eye,
  EyeOff,
  FileText,
  CheckCircle2,
  ShieldCheck,
  Loader2,
  Clock,
  MapPin,
  Hash,
  Map as MapIcon,
  AlertCircle,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import SEO from '../components/SEO';
import { LanguageProvider } from '../context/LanguageContext';
import { US_STATES } from '../utils/usStates';
import { formatUSPhone } from '../utils/addressValidation';
import AgreementCheckbox from '../components/AgreementCheckbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// iter96 — grouped organization list approved by the product owner.
// Each entry uses a stable `value` (snake_case slug) so analytics + Mongo
// queries stay consistent regardless of how the label is translated.
// "Other" reveals a free-text input so partners can type a custom name
// — that value is persisted as `organization_type_other` on the doc.
// iter97 — Flat alphabetical list (no optgroup category headers).
// "Other" is intentionally pinned to the end so users see it last.
const ORG_TYPE_OPTIONS = [
  { value: 'restaurantes_brasileiros_ma',              label: 'Associação de Restaurantes Brasileiros de Massachusetts' },
  { value: 'restaurantes_chineses_boston',             label: 'Associação de Restaurantes Chineses de Boston' },
  { value: 'restaurantes_coreanos_ne',                 label: 'Associação de Restaurantes Coreanos da Nova Inglaterra' },
  { value: 'restaurantes_indianos_ma',                 label: 'Associação de Restaurantes Indianos de Massachusetts' },
  { value: 'restaurantes_japoneses_boston',            label: 'Associação de Restaurantes Japoneses de Boston' },
  { value: 'restaurantes_mediterraneos_ne',            label: 'Associação de Restaurantes Mediterrâneos da Nova Inglaterra' },
  { value: 'restaurantes_portugueses_ne',              label: 'Associação de Restaurantes Portugueses da Nova Inglaterra' },
  { value: 'restaurantes_vietnamitas_ma',              label: 'Associação de Restaurantes Vietnamitas de Massachusetts' },
  { value: 'asian_american_business_council',          label: 'Asian American Business Council' },
  { value: 'brazilian_american_chamber',               label: 'Brazilian-American Chamber of Commerce' },
  { value: 'brazilian_business_alliance',              label: 'Brazilian Business Alliance' },
  { value: 'cambridge_chamber',                        label: 'Cambridge Chamber of Commerce' },
  { value: 'chelsea_chamber',                          label: 'Chelsea Chamber of Commerce' },
  { value: 'chinese_economic_development_council',     label: 'Chinese Economic Development Council' },
  { value: 'downtown_boston_business_association',     label: 'Downtown Boston Business Association' },
  { value: 'east_boston_chamber',                      label: 'East Boston Chamber of Commerce' },
  { value: 'everett_chamber',                          label: 'Everett Chamber of Commerce' },
  { value: 'greater_boston_chamber',                   label: 'Greater Boston Chamber of Commerce' },
  { value: 'international_pizza_expo',                 label: 'International Pizza Expo' },
  { value: 'italian_chamber_new_england',              label: 'Italian Chamber of Commerce of New England' },
  { value: 'japanese_american_society_boston',         label: 'Japanese American Society of Boston' },
  { value: 'korean_american_grocers_association_ne',   label: 'Korean American Grocers Association of New England' },
  { value: 'lynn_chamber',                             label: 'Lynn Area Chamber of Commerce' },
  { value: 'malden_chamber',                           label: 'Malden Chamber of Commerce' },
  { value: 'massachusetts_food_truck_association',     label: 'Massachusetts Food Truck Association' },
  { value: 'massachusetts_hispanic_chamber',           label: 'Massachusetts Hispanic Chamber of Commerce' },
  { value: 'massachusetts_lodging_association',        label: 'Massachusetts Lodging Association' },
  { value: 'massachusetts_restaurant_association',     label: 'Massachusetts Restaurant Association' },
  { value: 'medford_chamber',                          label: 'Medford Chamber of Commerce' },
  { value: 'nfib',                                     label: 'National Federation of Independent Business' },
  { value: 'national_italian_american_foundation',     label: 'National Italian American Foundation' },
  { value: 'national_restaurant_association',          label: 'National Restaurant Association' },
  { value: 'new_england_food_truck_association',       label: 'New England Food Truck Association' },
  { value: 'new_england_inns_resorts_association',     label: 'New England Inns and Resorts Association' },
  { value: 'pizza_today_community',                    label: 'Pizza Today Community' },
  { value: 'revere_chamber',                           label: 'Revere Chamber of Commerce' },
  { value: 'score_boston',                             label: 'SCORE Boston' },
  { value: 'small_business_association_new_england',   label: 'Small Business Association of New England' },
  { value: 'somerville_chamber',                       label: 'Somerville Chamber of Commerce' },
  { value: 'united_states_pizza_team',                 label: 'United States Pizza Team' },
  { value: 'us_hispanic_chamber',                      label: 'United States Hispanic Chamber of Commerce' },
  { value: 'vietnamese_american_civic_association',    label: 'Vietnamese American Civic Association' },
  { value: 'woburn_chamber',                           label: 'Woburn Chamber of Commerce' },
  // Pinned last
  { value: 'other',                                    label: 'Other — specify below' },
];

const FIELD_BASE =
  'w-full bg-black/40 border border-white/[0.10] focus:border-gold-500/60 rounded-xl px-4 py-3 text-[15px] text-white placeholder:text-white/35 outline-none transition-all';

function Field({ label, hint, error, required, children, testId }) {
  return (
    <label className="block" data-testid={testId ? `${testId}-wrap` : undefined}>
      <span className="block text-xs font-bold uppercase tracking-[0.18em] text-white/55 mb-2">
        {label} {required && <span className="text-gold-400">*</span>}
      </span>
      {children}
      {error && (
        <p className="mt-1.5 text-xs text-red-400" role="alert" data-testid="partners-field-error">
          {error}
        </p>
      )}
      {!error && hint && <p className="mt-1.5 text-xs text-white/40">{hint}</p>}
    </label>
  );
}

export default function PartnersOnboarding() {
  return (
    <LanguageProvider>
      <PartnersOnboardingInner />
    </LanguageProvider>
  );
}

function PartnersOnboardingInner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Phase 1 of the spec: public flow is application-only. Stripe Connect is
  // exposed exclusively inside the authenticated dashboard.
  //   Step 1: submit application
  //   Step 2: application received (awaiting approval)
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  // iter125 — Partner Agreement consent. Required before partner application
  // can be submitted; partners must accept the revenue-share terms.
  const [acceptedAgreement, setAcceptedAgreement] = useState(false);
  const [partner, setPartner] = useState(null);
  const [token, setToken] = useState(null);
  const [stripeStarting, setStripeStarting] = useState(false);
  const [showPw, setShowPw] = useState(false);

  // iter97 — flat alphabetical list (no optgroup category headers).
  const ORG_TYPES_I18N = ORG_TYPE_OPTIONS;

  const [form, setForm] = useState({
    organization_name: '',
    organization_type: '',
    organization_type_other: '',
    website: '',
    contact_name: '',
    email: '',
    phone: '',
    // iter95 — association headquarters address. Required so we can ship
    // physical marketing materials, route ticket escalations regionally,
    // and so the dashboard map can cluster partners geographically.
    address: '',
    zip_code: '',
    city: '',
    state: '',
    member_count: '',
    description: '',
    password: '',
    password_confirm: '',
  });
  const [errors, setErrors] = useState({});
  // ZIP-resolution status — same pattern used by RegisterPage and
  // AccountSettings so partners can't enter contradictory combinations
  // (e.g. ZIP 02149 + city "Kissimmee" + state FL).
  const [zipLookup, setZipLookup] = useState({ status: 'idle', city: '', state: '', error: '' });

  React.useEffect(() => {
    const zip = (form.zip_code || '').trim();
    let cancelled = false;
    const tid = setTimeout(() => {
      const run = async () => {
        if (!/^\d{5}$/.test(zip)) {
          setZipLookup({ status: 'idle', city: '', state: '', error: '' });
          return;
        }
        setZipLookup({ status: 'loading', city: '', state: '', error: '' });
        try {
          const r = await axios.get(`${API}/public/zip-lookup/${encodeURIComponent(zip)}`);
          if (cancelled) return;
          if (r.data?.valid) {
            const city = r.data.city || '';
            const state = (r.data.state || '').toUpperCase();
            setZipLookup({ status: 'ok', city, state, error: '' });
            setForm((f) => ({
              ...f,
              city: f.city || city,
              state: f.state || state,
            }));
            setErrors((p) => {
              const n = { ...p };
              delete n.zip_code; delete n.city; delete n.state;
              return n;
            });
          } else {
            setZipLookup({ status: 'invalid', city: '', state: '', error: 'Not a recognized US ZIP code.' });
          }
        } catch {
          if (!cancelled) setZipLookup({ status: 'invalid', city: '', state: '', error: 'Could not verify this ZIP code.' });
        }
      };
      run();
    }, 350);
    return () => { cancelled = true; clearTimeout(tid); };
  }, [form.zip_code]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Connect Stripe directly from the post-create welcome screen.
  const startStripeOnboarding = async () => {
    if (!token || stripeStarting) return;
    setStripeStarting(true);
    try {
      const { data } = await axios.post(
        `${API}/partners/stripe/connect-onboarding`,
        {
          return_path: '/partners/dashboard?stripe=return',
          refresh_path: '/partners/onboarding?stripe=refresh',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start Stripe onboarding');
        setStripeStarting(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Stripe error';
      toast.error(typeof detail === 'string' ? detail : 'Stripe error');
      setStripeStarting(false);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.organization_name || form.organization_name.length < 2)
      e.organization_name = t('partners.onboarding.err_org_required');
    // iter96 — organization_type is now a required choice (no default).
    if (!form.organization_type) e.organization_type = 'Please select your organization type';
    else if (form.organization_type === 'other' && !form.organization_type_other.trim())
      e.organization_type_other = 'Please tell us your organization name';
    if (!form.contact_name || form.contact_name.length < 2)
      e.contact_name = t('partners.onboarding.err_name_required');
    if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = t('partners.onboarding.err_email_invalid');
    if (!form.phone || form.phone.length < 6) e.phone = t('partners.onboarding.err_phone_required');
    if (!form.password || form.password.length < 8) e.password = t('partners.onboarding.err_password_min');
    else if (!/[A-Za-z]/.test(form.password) || !/\d/.test(form.password))
      e.password = t('partners.onboarding.err_password_format');
    if (!form.password_confirm) e.password_confirm = t('partners.onboarding.err_password_confirm');
    else if (form.password && form.password !== form.password_confirm)
      e.password_confirm = t('partners.onboarding.err_password_match');
    // iter95 — address required, with cross-validation against the ZIP.
    if (!form.address || form.address.length < 3) e.address = 'Street address is required';
    if (!/^\d{5}$/.test((form.zip_code || '').trim())) e.zip_code = 'Enter a valid 5-digit US ZIP code';
    if (!form.city) e.city = 'City is required';
    if (!form.state) e.state = 'Select your state';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error(t('partners.onboarding.toast_check_fields'));
      return;
    }
    // iter125 — Partner Agreement must be accepted before we POST.
    if (!acceptedAgreement) {
      setErrors((p) => ({ ...p, acceptedAgreement: 'Please review and accept the Partner Agreement to continue.' }));
      toast.error('Please review and accept the Partner Agreement to continue.');
      return;
    }
    // ── ZIP ↔ City ↔ State cross-validation (iter95) ─────────────────────
    // Exact same protection as the customer signup so partners can't save
    // contradictory combinations like ZIP 02149 + city Kissimmee + state FL.
    if (zipLookup.status === 'loading') {
      toast.error('Verifying your ZIP code — please wait a moment.');
      return;
    }
    if (zipLookup.status !== 'ok') {
      toast.error(zipLookup.error || 'Please enter a valid US ZIP code.');
      setErrors((p) => ({ ...p, zip_code: zipLookup.error || 'Invalid ZIP code' }));
      return;
    }
    if (form.state.toUpperCase() !== zipLookup.state) {
      const msg = `ZIP ${form.zip_code} is in ${zipLookup.state}, but you selected ${form.state}. Please fix the State or the ZIP code.`;
      setErrors((p) => ({ ...p, state: msg }));
      toast.error(msg);
      return;
    }
    // Final geocoding — confirms the FULL combination (street + city +
    // state + ZIP) is a real address.
    try {
      const v = await axios.post(`${API}/public/validate-address`, {
        street:   form.address.trim(),
        city:     form.city.trim(),
        state:    form.state.trim(),
        zip_code: form.zip_code.trim(),
      });
      if (!v.data?.valid) {
        const msg = (v.data?.error_message
          && `${v.data.error_message} (Expected ${zipLookup.city}, ${zipLookup.state} for ZIP ${form.zip_code}.)`)
          || `That address doesn't match ZIP ${form.zip_code}. Expected ${zipLookup.city}, ${zipLookup.state}.`;
        setErrors((p) => ({ ...p, address: msg }));
        toast.error(msg);
        return;
      }
    } catch {
      // Soft-fail on a Nominatim outage — backend still re-validates.
    }
    setSubmitting(true);
    try {
      // Drop fields that are local-only or need normalization.
      const { password_confirm: _ignored, ...rest } = form;
      const payload = {
        ...rest,
        state: (form.state || '').toUpperCase(),
        member_count: parseInt(form.member_count || 0, 10) || 0,
      };
      // iter96 — `organization_type_other` only travels when needed.
      if (payload.organization_type !== 'other') delete payload.organization_type_other;
      else payload.organization_type_other = (form.organization_type_other || '').trim();
      const { data } = await axios.post(`${API}/partners/register`, payload);
      // Persist the JWT so the partner can sign in to the dashboard later;
      // the dashboard itself shows "Pending review" until admin approves.
      localStorage.setItem('nofee_partner_token', data.token);
      setToken(data.token);
      setPartner(data.partner);
      // iter106 — Owner-approved UX change. Skip the "welcome / Connect
      // Stripe now" interstitial and send the new partner straight to
      // the dashboard, where the Stripe-Connect banner already lives.
      // The `?welcome=1` param triggers a one-time toast there explaining
      // the next step. This pattern (signup → dashboard → contextual
      // banner) is the recommended SaaS onboarding flow and avoids
      // dropping a banking-info CTA on a partner before they've seen any
      // platform value.
      toast.success(t('partners.onboarding.toast_received'));
      navigate('/partners/dashboard?welcome=1', { replace: true });
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let detail = '';
      if (Array.isArray(raw)) {
        detail = raw
          .map((d) => {
            const field = Array.isArray(d?.loc) ? d.loc.slice(-1)[0] : '';
            return field ? `${field}: ${d?.msg || 'invalid'}` : d?.msg;
          })
          .filter(Boolean)
          .join(' · ');
      } else if (typeof raw === 'string') {
        detail = raw;
      } else if (raw && typeof raw === 'object') {
        detail = raw.message || raw.msg || JSON.stringify(raw);
      }
      if (err?.response?.status === 409) {
        toast.error(t('partners.onboarding.toast_email_taken'));
        setErrors((p) => ({ ...p, email: t('partners.onboarding.err_email_taken') }));
      } else {
        toast.error(detail || t('partners.onboarding.toast_submit_error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white antialiased">
      <SEO
        title="Become a NoFee Partner"
        description="Apply to the NoFee Partners program — recurring revenue for restaurant associations, chambers of commerce, and food industry groups."
        path="/partners/onboarding"
        noindex
      />
      <Navbar forceSolid />

      <main className="pt-32 lg:pt-40 pb-20">
        <div className="max-w-[920px] mx-auto px-6 lg:px-8">
          <button
            onClick={() => navigate('/partners')}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/55 hover:text-gold-300 mb-6 transition-colors"
            data-testid="partners-onboarding-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {t('partners.onboarding.back')}
          </button>

          <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-bold mb-3">
            {t('partners.onboarding.kicker')}
          </p>
          <h1 className="text-3xl lg:text-5xl font-black tracking-tight text-white leading-[1.05] mb-3">
            {t('partners.onboarding.title')}
          </h1>
          <p className="text-white/60 max-w-[640px] mb-10">
            {t('partners.onboarding.subtitle')}
          </p>

          {/* ===================== STEP 1 — FORM ===================== */}
          {step === 1 && (
            <form
              onSubmit={onSubmit}
              className="rounded-3xl border border-white/[0.08] bg-gradient-to-b from-zinc-900/50 to-black/30 p-8 lg:p-10"
              data-testid="partners-onboarding-form"
            >
              <h2 className="text-xl lg:text-2xl font-black text-white mb-2">
                {t('partners.onboarding.form_title')}
              </h2>
              <p className="text-sm text-white/55 mb-7">
                {t('partners.onboarding.form_subtitle')}
              </p>

              <div className="grid lg:grid-cols-2 gap-5">
                <Field label={t('partners.onboarding.field_org_name')} required error={errors.organization_name}>
                  <div className="relative">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                    <input
                      value={form.organization_name}
                      onChange={(e) => update('organization_name', e.target.value)}
                      placeholder="Boston Restaurant Association"
                      className={`${FIELD_BASE} pl-11`}
                      data-testid="partners-field-organization-name"
                    />
                  </div>
                </Field>

                <Field label={t('partners.onboarding.field_org_type')} required error={errors.organization_type}>
                  <select
                    value={form.organization_type}
                    onChange={(e) => update('organization_type', e.target.value)}
                    className={FIELD_BASE}
                    data-testid="partners-field-organization-type"
                  >
                    <option value="" className="bg-zinc-900">Select your organization type…</option>
                    {ORG_TYPES_I18N.map((o) => (
                      <option key={o.value} value={o.value} className="bg-zinc-900">
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>

                {form.organization_type === 'other' && (
                  <Field
                    label="Organization name (Other)"
                    required
                    error={errors.organization_type_other}
                    hint="Type the official name of your organization."
                  >
                    <input
                      value={form.organization_type_other}
                      onChange={(e) => update('organization_type_other', e.target.value)}
                      placeholder="Your organization name"
                      className={FIELD_BASE}
                      maxLength={120}
                      data-testid="partners-field-organization-type-other"
                    />
                  </Field>
                )}

                <Field label={t('partners.onboarding.field_website')}>
                  <div className="relative">
                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                    <input
                      value={form.website}
                      onChange={(e) => update('website', e.target.value)}
                      placeholder="https://your-org.org"
                      className={`${FIELD_BASE} pl-11`}
                      data-testid="partners-field-website"
                    />
                  </div>
                </Field>

                <Field label={t('partners.onboarding.field_contact_name')} required error={errors.contact_name}>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                    <input
                      value={form.contact_name}
                      onChange={(e) => update('contact_name', e.target.value)}
                      placeholder="Jane Doe"
                      className={`${FIELD_BASE} pl-11`}
                      data-testid="partners-field-contact-name"
                    />
                  </div>
                </Field>

                <Field label={t('partners.onboarding.field_email')} required error={errors.email}>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                      placeholder="you@organization.org"
                      className={`${FIELD_BASE} pl-11`}
                      data-testid="partners-field-email"
                    />
                  </div>
                </Field>

                <Field label={t('partners.onboarding.field_phone')} required error={errors.phone}>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                    <input
                      value={form.phone}
                      onChange={(e) => update('phone', formatUSPhone(e.target.value))}
                      placeholder="(617) 555-0123"
                      className={`${FIELD_BASE} pl-11`}
                      maxLength={16}
                      data-testid="partners-field-phone"
                    />
                  </div>
                </Field>

                <Field label="Organization street address" required error={errors.address}>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                    <input
                      value={form.address}
                      onChange={(e) => update('address', e.target.value)}
                      placeholder="123 Main Street, Suite 200"
                      className={`${FIELD_BASE} pl-11`}
                      data-testid="partners-field-address"
                      autoComplete="street-address"
                    />
                  </div>
                </Field>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3">
                <Field label="ZIP" required error={errors.zip_code}>
                  <div className="relative">
                    <Hash className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                    <input
                      value={form.zip_code}
                      onChange={(e) => update('zip_code', e.target.value.replace(/\D/g, '').slice(0, 5))}
                      placeholder="02118"
                      className={`${FIELD_BASE} pl-11`}
                      inputMode="numeric"
                      maxLength={5}
                      data-testid="partners-field-zip"
                      autoComplete="postal-code"
                    />
                  </div>
                </Field>
                <Field label="City" required error={errors.city}>
                  <div className="relative">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                    <input
                      value={form.city}
                      onChange={(e) => update('city', e.target.value)}
                      placeholder="Boston"
                      className={`${FIELD_BASE} pl-11`}
                      data-testid="partners-field-city"
                      autoComplete="address-level2"
                    />
                  </div>
                </Field>
                <Field label="State" required error={errors.state}>
                  <div className="relative">
                    <MapIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35 pointer-events-none" />
                    <select
                      value={form.state}
                      onChange={(e) => update('state', e.target.value)}
                      className={`${FIELD_BASE} pl-11 appearance-none bg-no-repeat bg-[right_0.85rem_center] bg-[length:14px] pr-9`}
                      style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23a3a3a3' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")" }}
                      data-testid="partners-field-state"
                      autoComplete="address-level1"
                    >
                      <option value="" className="bg-zinc-900">State</option>
                      {US_STATES.map((s) => (
                        <option key={s.code} value={s.code} className="bg-zinc-900">{s.code} — {s.name}</option>
                      ))}
                    </select>
                  </div>
                </Field>
              </div>
              {zipLookup.status === 'loading' && (
                <p className="mt-2 text-[11.5px] text-white/50 flex items-center gap-1.5" data-testid="partners-zip-checking">
                  <Loader2 className="w-3 h-3 animate-spin" /> Verifying ZIP code…
                </p>
              )}
              {zipLookup.status === 'ok' && (
                <p className="mt-2 text-[11.5px] text-emerald-300 font-semibold flex items-center gap-1.5" data-testid="partners-zip-ok">
                  <CheckCircle2 className="w-3 h-3" /> Resolved to {zipLookup.city}, {zipLookup.state}
                </p>
              )}
              {zipLookup.status === 'invalid' && (
                <p className="mt-2 text-[11.5px] text-amber-300 font-semibold flex items-center gap-1.5" data-testid="partners-zip-invalid">
                  <AlertCircle className="w-3 h-3" /> {zipLookup.error}
                </p>
              )}

              <div className="mt-5 grid lg:grid-cols-2 gap-5">

                <Field label={t('partners.onboarding.field_member_count')} hint={t('partners.onboarding.field_member_count_hint')}>
                  <div className="relative">
                    <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                    <input
                      type="number"
                      min="0"
                      value={form.member_count}
                      onChange={(e) => update('member_count', e.target.value)}
                      placeholder="120"
                      className={`${FIELD_BASE} pl-11`}
                      data-testid="partners-field-member-count"
                    />
                  </div>
                </Field>

                <Field label={t('partners.onboarding.field_notes')} hint={t('partners.onboarding.field_notes_hint')}>
                  <div className="relative">
                    <FileText className="absolute left-4 top-3.5 w-4 h-4 text-white/35" />
                    <textarea
                      value={form.description}
                      onChange={(e) => update('description', e.target.value)}
                      rows={1}
                      placeholder="e.g., We represent 120 pizzerias in Greater Boston"
                      className={`${FIELD_BASE} pl-11 resize-none`}
                      data-testid="partners-field-notes"
                    />
                  </div>
                </Field>
              </div>

              {/* Security section */}
              <div className="mt-8 pt-7 border-t border-white/[0.06]">
                <div className="flex items-start gap-2 mb-5">
                  <Lock className="w-4 h-4 text-gold-300 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-white">{t('partners.onboarding.security_title')}</p>
                    <p className="text-xs text-white/55 mt-0.5 leading-relaxed max-w-[560px]">
                      {t('partners.onboarding.security_subtitle')}
                    </p>
                  </div>
                </div>
                <div className="grid lg:grid-cols-2 gap-5">
                  <Field
                    label={t('partners.onboarding.field_password')}
                    required
                    error={errors.password}
                    hint={t('partners.onboarding.field_password_hint')}
                  >
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                      <input
                        type={showPw ? 'text' : 'password'}
                        value={form.password}
                        onChange={(e) => update('password', e.target.value)}
                        placeholder="••••••••"
                        className={`${FIELD_BASE} pl-11 pr-11`}
                        data-testid="partners-field-password"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-lg flex items-center justify-center text-white/45 hover:text-gold-300 hover:bg-white/[0.05] transition-colors"
                        aria-label={showPw ? 'Hide password' : 'Show password'}
                        data-testid="partners-field-password-toggle"
                      >
                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </Field>

                  <Field label={t('partners.onboarding.field_password_confirm')} required error={errors.password_confirm}>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                      <input
                        type={showPw ? 'text' : 'password'}
                        value={form.password_confirm}
                        onChange={(e) => update('password_confirm', e.target.value)}
                        placeholder="••••••••"
                        className={`${FIELD_BASE} pl-11`}
                        data-testid="partners-field-password-confirm"
                        autoComplete="new-password"
                      />
                    </div>
                  </Field>
                </div>
              </div>

              <div className="mt-8">
                {/* iter125 — Partner Agreement consent. Mandatory. */}
                <AgreementCheckbox
                  checked={acceptedAgreement}
                  onChange={(v) => {
                    setAcceptedAgreement(v);
                    if (v) setErrors((er) => ({ ...er, acceptedAgreement: undefined }));
                  }}
                  agreementLabel="Partner Agreement"
                  agreementHref="/partner-agreement"
                  testid="partner-agreement-checkbox"
                  errorText={errors.acceptedAgreement}
                />
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-white/45 flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-gold-300" /> {t('partners.onboarding.trust')}
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 disabled:opacity-60 disabled:cursor-not-allowed text-black font-extrabold text-[15px] shadow-[0_12px_30px_-8px_rgba(212,175,55,0.55)] transition-all"
                  data-testid="partners-onboarding-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> {t('partners.onboarding.submitting')}
                    </>
                  ) : (
                    <>
                      {t('partners.onboarding.submit')} <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* ===================== STEP 2 — WELCOME ===================== */}
          {step === 2 && partner && (
            <div
              className="rounded-3xl border border-gold-500/30 bg-gradient-to-br from-gold-500/[0.06] to-black/30 p-8 lg:p-12"
              data-testid="partners-onboarding-received"
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-gold-500 text-black flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <p className="text-[10.5px] uppercase tracking-[0.24em] text-gold-400 font-bold mb-2">
                  {t('partners.onboarding.welcome_kicker')}
                </p>
                <h2 className="text-2xl lg:text-4xl font-black tracking-tight text-white mb-2">
                  {t('partners.onboarding.welcome_title_a')} <span className="text-gold-300">{t('partners.onboarding.welcome_title_b')}</span>
                </h2>
                <p className="text-white/65 max-w-[520px] mx-auto leading-relaxed">
                  {t('partners.onboarding.welcome_subtitle')}
                </p>
              </div>

              {/* Account summary */}
              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 max-w-[560px] mx-auto mb-8 grid grid-cols-2 gap-y-2 gap-x-4 text-xs">
                <span className="text-white/45">{t('partners.onboarding.welcome_organization')}</span>
                <span className="text-white font-semibold text-right">{partner.organization_name}</span>
                <span className="text-white/45">{t('partners.onboarding.welcome_partner_id')}</span>
                <span className="text-gold-200 font-mono text-right">{partner.partner_id}</span>
                <span className="text-white/45">{t('partners.onboarding.welcome_referral_code')}</span>
                <span className="text-gold-200 font-mono text-right">{partner.referral_code}</span>
              </div>

              {/* Next Step — Connect Stripe */}
              <div className="rounded-2xl border border-gold-500/30 bg-black/40 p-6 max-w-[560px] mx-auto mb-6">
                <p className="text-[10.5px] uppercase tracking-[0.24em] text-gold-400 font-bold mb-2">
                  {t('partners.onboarding.next_step')}
                </p>
                <h3 className="text-lg font-bold text-white mb-2">
                  {t('partners.onboarding.next_step_title')}
                </h3>
                <p className="text-xs text-white/55 leading-relaxed mb-5">
                  {t('partners.onboarding.next_step_body')}
                </p>

                <button
                  onClick={startStripeOnboarding}
                  disabled={stripeStarting}
                  className="w-full inline-flex items-center justify-center gap-3 h-13 px-7 py-3.5 rounded-2xl bg-[#635bff] hover:bg-[#7a72ff] disabled:opacity-60 text-white font-extrabold text-[15px] transition-all shadow-[0_12px_30px_-8px_rgba(99,91,255,0.45)]"
                  data-testid="partners-welcome-connect-stripe"
                >
                  {stripeStarting ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                  {t('partners.onboarding.connect_stripe')} <ArrowRight className="w-4 h-4" />
                </button>

                <div className="mt-4 pt-4 border-t border-white/[0.06] grid grid-cols-2 gap-2 text-[11px] text-white/55">
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck className="w-3 h-3 text-gold-300" /> {t('partners.onboarding.trust_powered')}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck className="w-3 h-3 text-gold-300" /> {t('partners.onboarding.trust_nobank')}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck className="w-3 h-3 text-gold-300" /> {t('partners.onboarding.trust_auto')}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck className="w-3 h-3 text-gold-300" /> {t('partners.onboarding.trust_realtime')}
                  </div>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={() => navigate('/partners/dashboard')}
                  className="text-xs font-semibold text-white/55 hover:text-gold-300 underline underline-offset-4"
                  data-testid="partners-welcome-skip-stripe"
                >
                  {t('partners.onboarding.skip')}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <FooterSection />
    </div>
  );
}
